// src/services/conferences.js
/**
 * ============================================================================
 * СЕРВИС КОНФЕРЕНЦИЙ
 * ============================================================================
 * Методы для работы с конференциями
 * ============================================================================
 */
import api from './api'
import coursesService from './courses'

const FALLBACK_ROUTE_STATUSES = new Set([404, 405, 501])
const AUTH_OR_RATE_LIMIT_STATUSES = new Set([401, 403, 429])

function getStatus(error) {
	return error?.response?.status
}

function isAuthOrRateLimitError(error) {
	return AUTH_OR_RATE_LIMIT_STATUSES.has(getStatus(error))
}

function canUseRouteFallback(error) {
	return FALLBACK_ROUTE_STATUSES.has(getStatus(error))
}

export const conferencesService = {
	/**
	 * Получить все конференции курса
	 */
	async getByCourseId(
		courseId,
		includePast = false,
		includeUnpublished = false
	) {
		const params = {}
		if (includeUnpublished) params.includeUnpublished = 'true'
		if (includePast) params.includePast = 'true'
		const response = await api.get(`/courses/${courseId}/conferences`, {
			params,
		})
		return response?.data ?? response
	},

	/**
	 * Получить конференцию по ID
	 */
	async getById(id) {
		const response = await api.get(`/conferences/${id}`)
		return response?.data ?? response
	},

	/**
	 * Создать новую конференцию
	 */
	async create(courseId, conferenceData) {
		const response = await api.post(
			`/courses/${courseId}/conferences`,
			conferenceData
		)
		return response?.data ?? response
	},

	/**
	 * Обновить конференцию
	 */
	async update(id, conferenceData) {
		const response = await api.put(`/conferences/${id}`, conferenceData)
		return response?.data ?? response
	},

	/**
	 * Удалить конференцию
	 */
	async delete(id) {
		const response = await api.delete(`/conferences/${id}`)
		return response?.data ?? response
	},

	/**
	 * Опубликовать конференцию
	 */
	async publish(id) {
		const response = await api.post(`/conferences/${id}/publish`)
		return response?.data ?? response
	},

	/**
	 * Зарегистрироваться на конференцию
	 */
	async register(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/register`)
		return response?.data ?? response
	},

	/**
	 * Отменить регистрацию
	 */
	async unregister(conferenceId) {
		const response = await api.delete(`/conferences/${conferenceId}/register`)
		return response?.data ?? response
	},

	/**
	 * Получить участников конференции
	 */
	async getParticipants(conferenceId) {
		const response = await api.get(`/conferences/${conferenceId}/participants`)
		return response?.data ?? response
	},

	/**
	 * Начать конференцию
	 */
	async start(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/start`)
		return response?.data ?? response
	},

	/**
	 * Завершить конференцию
	 */
	async complete(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/complete`)
		return response?.data ?? response
	},

	// Алиас для совместимости со стором
	async end(conferenceId) {
		return this.complete(conferenceId)
	},

	/**
	 * Отменить конференцию
	 */
	async cancel(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/cancel`)
		return response?.data ?? response
	},

	/**
	 * Отслеживание присутствия (join/leave)
	 */
	async trackAttendance(conferenceId, action) {
		const response = await api.post(`/conferences/${conferenceId}/attendance`, {
			action,
		})
		return response?.data ?? response
	},

	/**
	 * Получить токен Agora для конференции
	 */
	async getAgoraToken(conferenceId) {
		const response = await api.get(`/conferences/${conferenceId}/agora-token`)
		return response?.data ?? response
	},

	/**
	 * Получить конференции студента (из курсов, на которые он подписан)
	 * 🔥 Использует fallback через enrolled курсы
	 */
	async getMy(limit = null, includePast = false) {
		// Пробуем специальный endpoint
		try {
			const params = {}
			if (limit) params.limit = limit
			if (includePast) params.includePast = 'true'
			const response = await api.get('/conferences/my', { params })
			return response?.data ?? response
		} catch (err) {
			// Если 404 - используем fallback
			if (err.response?.status === 404) {
				console.warn(
					'[conferencesService] /conferences/my not found, using fallback'
				)
				return this.getStudentConferencesFallback(limit, includePast)
			}
			throw err
		}
	},

	/**
	 * Fallback для студенческих конференций
	 */
	async getStudentConferencesFallback(limit = null, includePast = false) {
		try {
			// Получаем enrolled курсы студента
			const coursesResponse = await api.get('/courses/enrolled')
			const courses = this._normalizeCourses(
				coursesResponse?.data ?? coursesResponse
			)

			if (courses.length === 0) {
				return []
			}

			// Загружаем конференции для каждого курса
			const allConferences = []

			for (const course of courses) {
				try {
					const conferences = await this.getByCourseId(course.id, includePast)
					const normalized = this._normalizeConferences(conferences)

					// Добавляем информацию о курсе к каждой конференции
					normalized.forEach(conf => {
						allConferences.push({
							...conf,
							course_id: course.id,
							course_title: course.title,
						})
					})
				} catch (err) {
					console.warn(
						`Failed to load conferences for course ${course.id}:`,
						err.message
					)
				}
			}

			// Сортируем по дате
			allConferences.sort(
				(a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)
			)

			// Применяем limit
			if (limit && limit > 0) {
				return allConferences.slice(0, limit)
			}

			return allConferences
		} catch (err) {
			console.error('[conferencesService] Fallback failed:', err)
			return []
		}
	},

	/**
	 * Конференции преподавателя (сгруппированные по курсам)
	 * 🔥 Использует fallback через курсы преподавателя
	 */
	async getTeacherConferences(includePast = false) {
		console.log(
			'[conferencesService] getTeacherConferences called, includePast:',
			includePast
		)

		// Пробуем специальный endpoint
		try {
			const params = includePast ? { includePast: 'true' } : {}
			console.log('[conferencesService] Trying /conferences/teacher...')
			const response = await api.get('/conferences/teacher', { params })
			console.log(
				'[conferencesService] /conferences/teacher response:',
				response
			)

			// 🔥 Нормализуем ответ
			let conferences = []
			const data = response?.data ?? response

			if (Array.isArray(data)) {
				conferences = data
			} else if (Array.isArray(data?.data)) {
				conferences = data.data
			}

			// 🔥 КЛЮЧЕВОЕ ИСПРАВЛЕНИЕ: если includePast=true и массив пустой - используем fallback
			// Потому что бэкенд может не поддерживать этот параметр
			if (includePast && conferences.length === 0) {
				console.log(
					'[conferencesService] API returned empty for includePast=true, trying fallback...'
				)
				return this.getTeacherConferencesFallback(includePast)
			}

			return conferences
		} catch (err) {
			console.log(
				'[conferencesService] /conferences/teacher error:',
				err.response?.status,
				err.message
			)
			// Для auth/rate-limit не запускаем fallback, чтобы не плодить лишние запросы
			if (isAuthOrRateLimitError(err)) {
				throw err
			}

			// Fallback нужен только если специализированного endpoint нет на бэкенде
			if (canUseRouteFallback(err)) {
				console.log('[conferencesService] Using fallback...')
				return this.getTeacherConferencesFallback(includePast)
			}

			throw err
		}
	},

	/**
	 * Fallback: загружаем конференции через курсы преподавателя
	 */
	async getTeacherConferencesFallback(includePast = false) {
		try {
			// Получаем курсы преподавателя
			let coursesResponse = null

			try {

				coursesResponse = await coursesService.getTeacherCourses()

			} catch (err) {
				if (isAuthOrRateLimitError(err)) {
					throw err
				}
				if (!canUseRouteFallback(err)) {
					throw err
				}

				console.warn(

					'[conferencesService] getTeacherCourses failed, falling back to /courses/teacher:',

					err?.message || err

				)

				const fallbackResponse = await api.get('/courses/teacher')

				coursesResponse = fallbackResponse?.data ?? fallbackResponse

			}

			const courses = this._normalizeCourses(coursesResponse)

			console.log(
				'[conferencesService] Teacher courses for fallback:',
				courses.length
			)

			if (courses.length === 0) {
				return []
			}

			// Загружаем конференции для каждого курса параллельно
			const results = await Promise.allSettled(
				courses.map(async course => {
					try {
						const conferences = await this.getByCourseId(
							course.id,
							includePast,
							true
						)
						const normalizedConferences =
							this._normalizeConferences(conferences)

						console.log(
							`[conferencesService] Course ${course.id} (${course.title}):`,
							normalizedConferences.length,
							'conferences, includePast:',
							includePast
						)

						return {
							course_id: course.id,
							course_title: course.title,
							course_category: course.category || course.category_name || '',
							conferences: normalizedConferences,
						}
					} catch (err) {
						console.warn(
							`Failed to load conferences for course ${course.id}:`,
							err.message
						)
						return {
							course_id: course.id,
							course_title: course.title,
							course_category: course.category || '',
							conferences: [],
						}
					}
				})
			)

			// Фильтруем успешные результаты с конференциями
			const grouped = results
				.filter(r => r.status === 'fulfilled')
				.map(r => r.value)
				.filter(group => group.conferences.length > 0)

			console.log(
				'[conferencesService] Fallback result:',
				grouped.length,
				'groups with conferences'
			)

			return grouped
		} catch (err) {
			console.error('[conferencesService] Teacher fallback failed:', err)
			return []
		}
	},

	/**
	 * Присоединиться к конференции (отметить присутствие)
	 */
	async join(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/attendance`, {
			action: 'join',
		})
		return response?.data ?? response
	},

	/**
	 * Покинуть конференцию
	 */
	async leave(conferenceId) {
		const response = await api.post(`/conferences/${conferenceId}/attendance`, {
			action: 'leave',
		})
		return response?.data ?? response
	},

	// ============ HELPERS ============

	/**
	 * Нормализация ответа с курсами
	 */
	_normalizeCourses(data) {
		if (Array.isArray(data)) return data
		if (Array.isArray(data?.data)) return data.data
		if (Array.isArray(data?.courses)) return data.courses
		if (Array.isArray(data?.data?.courses)) return data.data.courses
		return []
	},

	/**
	 * Нормализация ответа с конференциями
	 */
	_normalizeConferences(data) {
		if (Array.isArray(data)) return data
		if (Array.isArray(data?.data)) return data.data
		if (Array.isArray(data?.conferences)) return data.conferences
		return []
	},
}

// Алиас для совместимости
export const conferencesApi = conferencesService
export default conferencesService
